// API helpers for /api/auth/*. See packages/backend/src/routes/auth.ts.

import type { AuthTokensDto } from 'shared';

import { api, refreshSession, setAccessToken, setImpersonationMode } from './client.js';

export async function register(input: {
  email: string;
  password: string;
  name: string;
  company_name?: string;
}): Promise<void> {
  await api.post('/auth/register', input);
}

export async function verifyEmail(token: string): Promise<void> {
  await api.post('/auth/verify-email/confirm', { token });
}

export async function resendVerification(email: string): Promise<void> {
  await api.post('/auth/verify-email/resend', { email });
}

export async function login(input: {
  email: string;
  password: string;
}): Promise<AuthTokensDto> {
  const res = await api.post<AuthTokensDto>('/auth/login', input);
  setAccessToken(res.data.access_token);
  // A regular customer login clears any leftover impersonation mode
  // from a prior session in the same tab.
  setImpersonationMode(false);
  return res.data;
}

export async function logout(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } finally {
    setAccessToken(null);
    setImpersonationMode(false);
  }
}

// Customer-shaped view of the shared refresh chokepoint. The dedupe that keeps
// two concurrent callers from presenting the same single-use cookie — and
// burning the family as a replay — lives in client.refreshSession(), because it
// has to hold across every module that refreshes, not just this one.
export async function refresh(): Promise<AuthTokensDto | null> {
  const session = await refreshSession();
  if (!session) {
    setAccessToken(null);
    return null;
  }
  return {
    access_token: session.access_token,
    access_token_expires_in: session.access_token_expires_in,
    user: session.user,
  };
}

export async function confirmAccountUnlock(token: string): Promise<void> {
  await api.post('/auth/account-unlock/confirm', { token });
}

export async function requestPasswordReset(email: string): Promise<void> {
  await api.post('/auth/password-reset/start', { email });
}

export async function confirmPasswordReset(token: string, new_password: string): Promise<void> {
  await api.post('/auth/password-reset/confirm', { token, new_password });
}

export type InvitationPreview = {
  email: string;
  invited_by_id: string | null;
  expires_at: string;
};

export async function previewInvitation(token: string): Promise<InvitationPreview> {
  const res = await api.post<{ invitation: InvitationPreview }>('/auth/invite/preview', {
    token,
  });
  return res.data.invitation;
}

export async function acceptInvitation(input: {
  token: string;
  password: string;
}): Promise<AuthTokensDto> {
  const res = await api.post<AuthTokensDto>('/auth/invite/accept', input);
  setAccessToken(res.data.access_token);
  return res.data;
}
